import { Button, FormControl, FormHelperText, IconButton, InputLabel, Select, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { AddRounded as AddIcon, DeleteRounded as DeleteIcon } from '@material-ui/icons';

import { StorableDay } from '../../../delivery-points/types/delivery-point-day.type';
import { STORABLE_DAY_OPTIONS } from '../../../delivery-points/utils/format-schedule-days.util';
import { DeliveryCitySlot } from '../../types/delivery-city.type';
import { getSlotErrors } from '../../utils/delivery-city-form-rules.util';

type Props = {
  slots: DeliveryCitySlot[];
  onChange: (slots: DeliveryCitySlot[]) => void;
};

type SlotTimeField = 'from' | 'to';

const NEW_SLOT: DeliveryCitySlot = { day: 'monday', from: '10:30', to: '12:30' };

const TIME_INPUT_PROPS = { step: 600 };

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',

    '& .MuiButton-root': {
      textTransform: 'none',
    },
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  fields: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',

    '& > .MuiFormControl-root': {
      flex: 1,
    },
  },
  addButton: {
    alignSelf: 'flex-start',
  },
});

export function DeliveryCitySlotsEditor({ slots, onChange }: Props) {
  const classes = useStyles();
  const slotErrors = getSlotErrors(slots);

  const handleSlotChange = (changedIndex: number, changedSlot: DeliveryCitySlot) =>
    onChange(slots.map((slot, index) => (index === changedIndex ? changedSlot : slot)));

  const handleTimeChange = (changedIndex: number, field: SlotTimeField, time: string) =>
    handleSlotChange(changedIndex, { ...slots[changedIndex], [field]: time });

  const handleRemove = (removedIndex: number) => onChange(slots.filter((slot, index) => index !== removedIndex));

  const handleAdd = () => onChange([...slots, { ...NEW_SLOT }]);

  return (
    <div className={classes.root}>
      <ul className={classes.list}>
        {slots.map((slot, index) => (
          <li key={index} className={classes.row}>
            <div className={classes.fields}>
              <FormControl variant="outlined" size="small">
                <InputLabel htmlFor={`delivery-city-slot-${index}-day`}>Jour</InputLabel>
                <Select
                  native
                  label="Jour"
                  value={slot.day}
                  inputProps={{ id: `delivery-city-slot-${index}-day` }}
                  onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                    handleSlotChange(index, { ...slot, day: event.target.value as StorableDay })
                  }
                >
                  {STORABLE_DAY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <TextField
                id={`delivery-city-slot-${index}-from`}
                label="De"
                type="time"
                size="small"
                variant="outlined"
                error={slotErrors[index] !== null}
                InputLabelProps={{ shrink: true }}
                inputProps={TIME_INPUT_PROPS}
                value={slot.from}
                onChange={(event) => handleTimeChange(index, 'from', event.target.value)}
              />
              <TextField
                id={`delivery-city-slot-${index}-to`}
                label="À"
                type="time"
                size="small"
                variant="outlined"
                error={slotErrors[index] !== null}
                InputLabelProps={{ shrink: true }}
                inputProps={TIME_INPUT_PROPS}
                value={slot.to}
                onChange={(event) => handleTimeChange(index, 'to', event.target.value)}
              />
              <IconButton size="small" title="Supprimer le créneau" onClick={() => handleRemove(index)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
            {slotErrors[index] !== null && <FormHelperText error>{slotErrors[index]}</FormHelperText>}
          </li>
        ))}
      </ul>
      <Button size="small" variant="outlined" className={classes.addButton} startIcon={<AddIcon />} onClick={handleAdd}>
        Ajouter un créneau
      </Button>
    </div>
  );
}
